/**
 * useParallax — returns a translateY value (px) based on the element's
 * distance from the centre of the viewport.
 *
 * v34.1 (Mar 2026): used on the hero dashboard mock so it drifts gently
 * upward as the visitor scrolls past it. Subtle by design — strength=0.15
 * gives ~30px max travel before the element leaves the fold.
 *
 *   const { ref, offset } = useParallax(0.15);
 *   <div ref={ref} style={{ transform: `translate3d(0, ${offset}px, 0)` }} />
 */
import { useEffect, useRef, useState } from 'react';

export default function useParallax(strength = 0.15) {
  const ref = useRef(null);
  const [offset, setOffset] = useState(0);
  const rafRef = useRef(null);

  useEffect(() => {
    if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      return undefined;
    }

    const update = () => {
      const el = ref.current;
      if (!el) {
        rafRef.current = null;
        return;
      }
      const rect = el.getBoundingClientRect();
      const center = rect.top + rect.height / 2;
      const viewportCenter = window.innerHeight / 2;
      const diff = center - viewportCenter;
      setOffset(-diff * strength);
      rafRef.current = null;
    };

    const onScroll = () => {
      if (rafRef.current === null) rafRef.current = requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, [strength]);

  return { ref, offset };
}
